import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import url from './url';

const pageLabels = {
    faq: 'FAQs',
    terms: 'Terms & conditions',
    privacy: 'Privacy policy',
};

const parseSections = (body = '') => {
    const blocks = body.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);
    const sections = [];

    blocks.forEach((block) => {
        if (block.startsWith('## ')) {
            const [headingLine, ...contentLines] = block.split('\n');
            sections.push({
                heading: headingLine.slice(3).trim(),
                content: contentLines.join('\n').trim() ? [contentLines.join('\n').trim()] : [],
            });
            return;
        }
        if (!sections.length) sections.push({ heading: '', content: [] });
        sections[sections.length - 1].content.push(block);
    });

    return sections;
};

const ContentPage = ({ slug }) => {
    const [page, setPage] = useState(null);
    const [error, setError] = useState('');
    const sections = useMemo(() => parseSections(page?.body), [page]);

    useEffect(() => {
        let active = true;
        setPage(null);
        setError('');

        fetch(`${url}/api/site-content/${slug}`)
            .then(async (response) => {
                const result = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(result.error || 'Unable to load this page');
                if (active) setPage(result);
            })
            .catch((fetchError) => {
                if (active) setError(fetchError.message);
            });

        return () => { active = false; };
    }, [slug]);

    return (
        <main className="content-page">
            <header className="content-page-hero">
                <div className="container">
                    <span className="section-kicker">COURIERLINK INFORMATION</span>
                    <h1>{page?.title || pageLabels[slug]}</h1>
                    <p>Clear information for confident deliveries.</p>
                </div>
            </header>

            <div className="container content-page-layout">
                <nav className="content-page-nav" aria-label="Information pages">
                    <span>On this site</span>
                    {Object.entries(pageLabels).map(([path, label]) => (
                        <NavLink key={path} to={`/${path}`} className={({ isActive }) => isActive ? 'active' : ''}>{label}</NavLink>
                    ))}
                </nav>

                <article className={`content-page-body ${slug === 'faq' ? 'content-page-faq' : ''}`}>
                    {!page && !error && <div className="content-page-message">Loading page…</div>}
                    {error && <div className="content-page-message content-page-error">{error}</div>}
                    {page && sections.map((section, index) => (
                        slug === 'faq' ? (
                            <details key={`${section.heading}-${index}`} className="faq-item" open={index === 0}>
                                <summary>{section.heading || 'More information'}<span aria-hidden="true">+</span></summary>
                                <div>{section.content.map((paragraph, paragraphIndex) => <p key={paragraphIndex}>{paragraph}</p>)}</div>
                            </details>
                        ) : (
                            <section key={`${section.heading}-${index}`} className="legal-section">
                                {section.heading && <h2>{section.heading}</h2>}
                                {section.content.map((paragraph, paragraphIndex) => <p key={paragraphIndex}>{paragraph}</p>)}
                            </section>
                        )
                    ))}
                    {page?.updatedAt && (
                        <p className="content-page-updated">Last updated {new Date(page.updatedAt).toLocaleDateString('en-AU')}</p>
                    )}
                </article>
            </div>
        </main>
    );
};

export default ContentPage;